import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PagoCuotasFinanciacionPropiaModel,
  ConstitucionFinanciacionPropiaModel,
  FinanciacionPropiaModel,
  DeudaACortoPlazoFinanciacionPropiaModel
} from './financiacion-propia.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const FINANCIACIONPROPIA_ENDPOINT = '/financiacionpropia';
const CONSTITUCIONFINANCIACIONPROPIA_ENDPOINT = '/constitucionfinanciacionpropia';
const DEUDACORTOPLAZO_ENDPOINT = '/deudasacortoplazofinanciacionpropia';
const PAGOCUOTAS_ENDPOINT = '/pagocuotasfinanciacionpropia';
const V2_ENDPOINT = '/api/v2/own-financings';

@Injectable({
  providedIn: 'root',
})
export class FinanciacionPropiaService {
  constructor(private http: HttpClient) {}

  public downloadFileAttachment(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('financiacionpropiaId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/financiacionpropia/file/downloadexcel', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/vnd.ms-excel' });
    });
  }

  public get(uid: string): Promise<FinanciacionPropiaModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<FinanciacionPropiaModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se ha encontrado la financiación propia.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<FinanciacionPropiaModel>> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<FinanciacionPropiaModel[]>>(environment.apiUrl + V2_ENDPOINT)),
      'No se pudieron obtener las financiaciones propias.'
    ).then((list) => {
      const result = new ResultModel<FinanciacionPropiaModel>();
      result.list = Array.isArray(list) ? list : [];
      result.totalItems = result.list.length;
      return result;
    });
  }

  public add(financiacionpropia: FinanciacionPropiaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPROPIA_ENDPOINT, financiacionpropia)
    ).then((response) => response.data);
  }

  public edit(financiacionpropia: FinanciacionPropiaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPROPIA_ENDPOINT, financiacionpropia)
    ).then((response) => response.data);
  }

  public editFileAttachment(financiacionpropia: FinanciacionPropiaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(
        environment.apiUrl + FINANCIACIONPROPIA_ENDPOINT + '/file',
        financiacionpropia
      )
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('financiacionpropiaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPROPIA_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public constitucionList(
    financiacionpropiaId: string
  ): Promise<ConstitucionFinanciacionPropiaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<ConstitucionFinanciacionPropiaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(financiacionpropiaId) + '/constitutions'
        )
      ),
      'No se pudieron obtener las constituciones.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public constitucionCreate(
    constitucion: ConstitucionFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(
        environment.apiUrl + CONSTITUCIONFINANCIACIONPROPIA_ENDPOINT,
        constitucion
      )
    ).then((response) => response.data);
  }

  public constitucionUpdate(
    constitucion: ConstitucionFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(
        environment.apiUrl + CONSTITUCIONFINANCIACIONPROPIA_ENDPOINT,
        constitucion
      )
    ).then((response) => response.data);
  }

  public constitucionDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('constitucionfinanciacionpropiaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONFINANCIACIONPROPIA_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public deudaACortoPlazoList(
    deudaacortoplazoUid: string
  ): Promise<DeudaACortoPlazoFinanciacionPropiaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<DeudaACortoPlazoFinanciacionPropiaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(deudaacortoplazoUid) + '/short-term-debts'
        )
      ),
      'No se pudieron obtener las deudas a corto plazo.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public deudaACortoPlazoCreate(
    deudaACortoPlazo: DeudaACortoPlazoFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoUpdate(
    deudaACortoPlazo: DeudaACortoPlazoFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('deudasacortoplazofinanciacionpropiaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public pagoCoutasList(
    pagocuotasUid: string
  ): Promise<PagoCuotasFinanciacionPropiaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<PagoCuotasFinanciacionPropiaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(pagocuotasUid) + '/installment-payments'
        )
      ),
      'No se pudieron obtener los pagos de cuotas.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public pagoCoutasCreate(
    pagoDeCuotas: PagoCuotasFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasUpdate(
    pagoDeCuotas: PagoCuotasFinanciacionPropiaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('pagocuotasfinanciacionpropiaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}